










































































































import CartInNotification from '@/components/organisms/dialog/ErrorDialog.vue'
import BehaviorConfig from '@/models/app-config/behavior/behavior'
import AddItemToCart from '@/models/cart/command/add-item-to-cart'
import ContactLensSKU from '@/models/item/sku/contact-lens'
import { DefaultFetchSkuPrice } from '@/models/item/sku/repository/impl/fetch-sku-price'
import { AssociationService } from '@/services/api/external-account-association/association'
import AppId from '@/util/appid'
import { BrandRouteResolver } from '@/util/brandRouteResolver'
import { DataLayerService } from '@/vendor/gtm/data-layer'
import { addToCartEventFactory } from '@/vendor/gtm/events/ga4/add-to-cart'
import { CartService } from '@ajax/modules/services/cart'
import { ProductService as AjaxProductService } from '@ajax/modules/services/product'
import Util from '@ajax/modules/util'
import { addLensToCart } from '@ajax/vue/components/organisms/contact-lens/model/add-lens-to-cart'
import Newstock from '@ajax/vue/components/templates/Newstock.vue'
import Restock from '@ajax/vue/components/templates/Restock.vue'
import { CartPageURL } from '@spa/components/pages/cart/model/Cart'
import { log } from '@spa/log'
import Vue from 'vue'
import { Component, Prop } from 'vue-property-decorator'
import ContactLensItemDetailStore from './store'

interface LineAccountAssociationLoadingState {
  isLoading: true
}

interface LineAccountAssociationLoadSuccessState {
  isLoading: false
  isAssociated: boolean
  loadError: undefined
}

interface LineAccountAssociationLoadFailureState {
  isLoading: false
  isAssociated: false
  loadError: Error
}

type LineAccountAssociationState =
  | LineAccountAssociationLoadingState
  | LineAccountAssociationLoadSuccessState
  | LineAccountAssociationLoadFailureState

@Component({
  components: {
    CartInNotification,
    Newstock,
    Restock,
  },
})
/**
 * コンタクトレンズ商品専用のカート追加UI
 * @see {AddItemToCart} システム標準のカート追加UI
 * @note ajaxからsrc/components配下に移動する.
 */
export default class ContactLensCartIn extends Vue {
  @Prop({ required: true })
  id: string

  @Prop({ required: true })
  store: ContactLensItemDetailStore | null

  @Prop({ default: undefined })
  displayPriceMode: 'simple' | undefined

  lineAccountAssociationState: LineAccountAssociationState = { isLoading: true }

  addToCartStatus: ProcessStatus = ProcessStatus.asNotProcessing()
  cartInNotificationMsg: string = ''

  get brandName(): string {
    return BrandRouteResolver.resolveBrandFromPathElement(
      this.$route.params.brand_english_name
    )
  }
  get cartService(): CartService {
    return CartService.createFromBrandEnglishName(this.brandName)
  }
  get ajaxProductService(): AjaxProductService {
    return new AjaxProductService(AppId.getByBrandName(this.brandName))
  }
  get dataLayerService(): DataLayerService {
    return new DataLayerService()
  }

  get isLineAccountAssociated(): boolean {
    const state = this.lineAccountAssociationState

    return !state.isLoading && state.isAssociated
  }

  get lineAssociationError(): boolean {
    const state = this.lineAccountAssociationState

    return !state.isLoading && state.loadError !== undefined
  }

  async created() {
    // bfcache対策
    // https://github.com/my-color/front/issues/6423
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        this.addToCartStatus = ProcessStatus.asNotProcessing()
        this.$forceUpdate()
      }
    })

    // Optional APIs: 失敗しても処理継続
    try {
      await Promise.all([this.initializeAccountAssociation()])
    } catch (error) {
      log.error(
        'Optional API failed - continuing with reduced functionality',
        error
      )
    }
  }

  get displayTotalCount(): boolean {
    return (
      this.selectedTotalBoxCount !== null &&
      this.selectedTotalLensCount !== null
    )
  }

  get selectedTotalBoxCount(): number | null {
    return this.store ? this.store.selectedTotalBoxCount : null
  }

  get selectedTotalLensCount(): number | null {
    return this.store ? this.store.selectedTotalLensCount : null
  }

  get totalPrice(): string | null {
    return this.store ? Util.formatJPYPrice(this.store.totalPrice) : null
  }

  get buttonClassesStr(): string {
    const classes = [
      'c-btn c-btn--primary c-btn--w-large c-btn--h-xlarge c-btn--center c-btn--effect__pop cart-in__button',
    ]
    if (
      !this.selectedTotalBoxCount ||
      !(this.store && this.store.selectedSKUsAreOverRegularSaleStart)
    ) {
      classes.push('disabled')
    }

    return classes.join(' ')
  }

  get leftSku(): ContactLensSKU | null {
    return this.store ? this.store.selectedLeftSku : null
  }

  get brandEnglishName(): string {
    return this.store ? this.store.root.brandEnglishName : ''
  }

  get displayNormalButton(): boolean {
    return this.buttonType === 'normal'
  }

  get displayNewItemRequestButton(): boolean {
    return this.buttonType === 'request_new_item'
  }

  get displayRestockRequestButton(): boolean {
    return this.buttonType === 'request_restock'
  }

  get newItemRequest(): ContactLensSKU | null {
    return this.store ? this.store.newItemRequest : null
  }

  get restockRequest(): ContactLensSKU | null {
    return this.store ? this.store.restockRequest(this.behavior) : null
  }

  async onClickCartIn(): Promise<void> {
    if (this.notificationIfShortOfLeftOrRightSku()) {
      return this.displayCartInNotification(
        this.notificationIfShortOfLeftOrRightSku()
      )
    }

    return this.startCartInProcess()
  }

  /**
   * カート追加時のnotificationを閉じる。
   * 閉じた後、カート追加処理を開始する。
   */
  closeCartInNotificationAndContinueProcess(): void {
    this.cartInNotificationMsg = ''
    this.startCartInProcess()
  }

  private get behavior(): BehaviorConfig {
    const brandName = BrandRouteResolver.resolveBrandFromPathElement(
      this.$route.params.brand_english_name
    )

    return BehaviorConfig.createFromBrand(brandName)
  }

  private get buttonType(): 'normal' | 'request_new_item' | 'request_restock' | null {
    if (!this.store) {
      return null
    }

    if (this.newItemRequest) {
      return 'request_new_item'
    }

    if (this.restockRequest) {
      return 'request_restock'
    }

    return 'normal'
  }

  /**
   * 連携情報の取得
   */
  private async initializeAccountAssociation(): Promise<void> {
    const associationService = new AssociationService()
    const result = await associationService.list(this.brandName, {
      serviceId: 'line',
    })
    result.fold(
      (error) => {
        log.error('Failed to fetch LINE account association:', error)
        this.lineAccountAssociationState = {
          isLoading: false,
          isAssociated: false,
          loadError: error,
        }
      },
      (list) => {
        this.lineAccountAssociationState = {
          isLoading: false,
          isAssociated: list.length > 0,
          loadError: undefined,
        }
      }
    )
  }

  /**
   * カート追加処理
   *
   * 左目SKUまたは右目SKUのいずれかのカート追加に失敗した場合、エラーモーダルを表示する。
   * それ以外の場合には、カート画面に遷移する。
   */
  private async startCartInProcess(): Promise<void> {
    const store = this.store
    if (store === null) {
      log.error(
        `store not initialized yet, but store must be initialized already when user can trigger this process`
      )

      return
    }
    this.addToCartStatus = ProcessStatus.asProcessing()

    addLensToCart(
      store,
      DefaultFetchSkuPrice.create(this.brandEnglishName),
      new AddItemToCart(this.cartService, this.ajaxProductService)
    )
      .then(result =>
        result.fold(
          (errorMessages) => {
            /**
             * TODO: <br>で結合した状態ではなく、単なる string[] でエラーメッセージが返ってくるようにする.
             * 元のエラーメッセージを返す機構が、最初から <br> でjoinした状態で返す仕組みになっている.
             * 既に結合されているため、重複したメッセージを取り除こうと思うと、↓のように split -> join という順を踏まなくてはならない.
             * また、エラーメッセージが <br/> や \n ではなく <br> で区切られている、という内部詳細とも
             * 暗黙的に結合することを強制されている.
             *
             * 内部で勝手に結合するのではなく、単なる string[] で返して、
             * あとは呼び出し元で都合の良いように加工する形を取れば、こうした問題は解消可能.
             * 呼び出し元で必要に応じて join('<br>') を行う必要はあるが、元々大した手間ではないし、
             * <br> 以外で結合したい or そもそも結合したくないケースにも、無理なく対応できる.
             */

            this.$store.commit('error/set', {
              message: errorMessages,
            })
            this.addToCartStatus = ProcessStatus.asNotProcessing()
          },
          (results) => {
            this.dataLayerService.push(
              addToCartEventFactory.createFromAddItemToCartResults(results),
              () => {
                location.href = CartPageURL.forBrand(
                  store.root.brandEnglishName,
                  { isCartIn: 'true' }
                ).href
              }
            )
          }
        )
      )
      .catch((reason) => {
        log.error(reason)

        this.$store.commit('error/set', {
          message: 'カート追加が失敗しました。',
        })

        this.addToCartStatus = ProcessStatus.asNotProcessing()
      })
  }

  /**
   * カート追加時のnotificationを表示する。
   */
  private displayCartInNotification(message: string) {
    this.cartInNotificationMsg = message
  }

  /**
   * 選択数量が、左目または右目の選択可能上限数を上回る場合に、「在庫切れのためカートに追加できない商品がありました。」
   * というメッセージを表示。
   */
  private notificationIfShortOfLeftOrRightSku(): string {
    return this.store &&
      this.store
        .selectedQuantityIsLargerThanMaxSelectableQuantityOfLeftOrRightSku
      ? '在庫切れのためカートに追加できない商品がありました。'
      : ''
  }
}

class ProcessStatus {
  static asProcessing(): ProcessStatus {
    return new ProcessStatus(true)
  }

  static asNotProcessing(): ProcessStatus {
    return new ProcessStatus(false)
  }

  private constructor(readonly inProcessing: boolean) {}
}
